// src/components/employee/PerformanceAnalytics.js
import React, { useState, useEffect } from 'react';
import {
    LineChart, Line, BarChart, Bar, RadarChart, PolarGrid, PolarAngleAxis,
    PolarRadiusAxis, Radar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer
} from 'recharts';
import { TrendingUp, BarChart3, Loader, AlertCircle, Filter } from 'lucide-react';
import performanceService from '../../services/performance.service';

const PerformanceAnalytics = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cycleFilter, setCycleFilter] = useState('all'); // 'all', 'monthly', 'quarterly', 'annual'
    const [reviewTypeFilter, setReviewTypeFilter] = useState('all'); // 'all', 'self_review', 'manager_review'

    useEffect(() => {
        fetchReviews();
    }, []);

    const fetchReviews = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await performanceService.getMyReviews();
            setReviews(data.reviews || []);
        } catch (err) {
            console.error('Error fetching reviews:', err);
            setError('Failed to load performance data');
        } finally {
            setLoading(false);
        }
    };

    // Filter reviews based on selected filters
    const getFilteredReviews = (reviewType) => {
        return reviews.filter(review => {
            const matchesCycle = cycleFilter === 'all' || review.review_cycle === cycleFilter;
            const matchesType = review.review_type === reviewType;
            return matchesCycle && matchesType && review.status === 'completed';
        });
    };

    const selfReviews = getFilteredReviews('self_review');
    const managerReviews = getFilteredReviews('manager_review');

    // Prepare data for trend chart (line chart)
    const prepareTrendData = (reviewsData) => {
        return reviewsData
            .sort((a, b) => new Date(a.review_period_end) - new Date(b.review_period_end))
            .map(review => ({
                date: new Date(review.review_period_end).toLocaleDateString('en-US', {
                    month: 'short',
                    year: 'numeric'
                }),
                cycle: review.review_cycle,
                overall: parseFloat(review.overall_rating),
                technical: parseFloat(review.technical_skills),
                communication: parseFloat(review.communication_skills),
                teamwork: parseFloat(review.teamwork),
                problemSolving: parseFloat(review.problem_solving),
                leadership: parseFloat(review.leadership)
            }));
    };

    // Prepare data for skills comparison (bar chart)
    const prepareSkillsComparison = (reviewsData) => {
        if (reviewsData.length === 0) return [];

        const latest = reviewsData[reviewsData.length - 1];
        return [
            { skill: 'Technical', rating: parseFloat(latest.technical_skills) },
            { skill: 'Communication', rating: parseFloat(latest.communication_skills) },
            { skill: 'Teamwork', rating: parseFloat(latest.teamwork) },
            { skill: 'Problem Solving', rating: parseFloat(latest.problem_solving) },
            { skill: 'Leadership', rating: parseFloat(latest.leadership) }
        ];
    };

    // Prepare data for radar chart (skills overview)
    const prepareRadarData = (selfData, managerData) => {
        const skills = ['Technical', 'Communication', 'Teamwork', 'Problem Solving', 'Leadership'];
        const fieldMap = {
            'Technical': 'technical_skills',
            'Communication': 'communication_skills',
            'Teamwork': 'teamwork',
            'Problem Solving': 'problem_solving',
            'Leadership': 'leadership'
        };

        return skills.map(skill => {
            const field = fieldMap[skill];
            const selfLatest = selfData.length > 0 ? selfData[selfData.length - 1] : null;
            const managerLatest = managerData.length > 0 ? managerData[managerData.length - 1] : null;

            return {
                skill,
                'Self Rating': selfLatest ? parseFloat(selfLatest[field]) : 0,
                'Manager Rating': managerLatest ? parseFloat(managerLatest[field]) : 0
            };
        });
    };

    const selfTrendData = prepareTrendData(selfReviews);
    const managerTrendData = prepareTrendData(managerReviews);
    const selfSkillsData = prepareSkillsComparison(selfReviews);
    const managerSkillsData = prepareSkillsComparison(managerReviews);
    const radarData = prepareRadarData(selfReviews, managerReviews);

    // Custom tooltip for charts
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} className="text-xs text-gray-600 dark:text-gray-400">
                            <span style={{ color: entry.color }}>�</span> {entry.name}: {entry.value.toFixed(1)}/5.0
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
        );
    }

    if (reviews.length === 0) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                <BarChart3 className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Performance Data</h3>
                <p className="text-gray-600 dark:text-gray-400">
                    You don't have any completed performance reviews yet.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <TrendingUp className="w-6 h-6" />
                        Performance Analytics
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Track your performance trends and skill development
                    </p>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-gray-500" />
                        <select
                            value={cycleFilter}
                            onChange={(e) => setCycleFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="all">All Cycles</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="annual">Annual</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Self Reviews</p>
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{selfReviews.length}</p>
                    {selfReviews.length > 0 && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                            Latest: {parseFloat(selfReviews[selfReviews.length - 1]?.overall_rating).toFixed(1)}/5.0
                        </p>
                    )}
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Manager Reviews</p>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">{managerReviews.length}</p>
                    {managerReviews.length > 0 && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                            Latest: {parseFloat(managerReviews[managerReviews.length - 1]?.overall_rating).toFixed(1)}/5.0
                        </p>
                    )}
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Average Performance</p>
                    <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                        {(reviews.reduce((sum, r) => sum + parseFloat(r.overall_rating), 0) / reviews.length).toFixed(1)}/5.0
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        Across all reviews
                    </p>
                </div>
            </div>

            {/* Self-Review Performance Trend */}
            {selfReviews.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Self-Review Performance Trend
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={selfTrendData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                            <XAxis
                                dataKey="date"
                                stroke="#6B7280"
                                style={{ fontSize: '12px' }}
                            />
                            <YAxis
                                domain={[0, 5]}
                                stroke="#6B7280"
                                style={{ fontSize: '12px' }}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="overall"
                                stroke="#3B82F6"
                                name="Overall Rating"
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                activeDot={{ r: 6 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="technical"
                                stroke="#10B981"
                                name="Technical"
                                strokeWidth={1}
                                dot={{ r: 3 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="communication"
                                stroke="#F59E0B"
                                name="Communication"
                                strokeWidth={1}
                                dot={{ r: 3 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="teamwork"
                                stroke="#8B5CF6"
                                name="Teamwork"
                                strokeWidth={1}
                                dot={{ r: 3 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Manager Review Performance Trend */}
            {managerReviews.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Manager Review Performance Trend
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={managerTrendData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                            <XAxis
                                dataKey="date"
                                stroke="#6B7280"
                                style={{ fontSize: '12px' }}
                            />
                            <YAxis
                                domain={[0, 5]}
                                stroke="#6B7280"
                                style={{ fontSize: '12px' }}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="overall"
                                stroke="#10B981"
                                name="Overall Rating"
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                activeDot={{ r: 6 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="technical"
                                stroke="#3B82F6"
                                name="Technical"
                                strokeWidth={1}
                                dot={{ r: 3 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="communication"
                                stroke="#F59E0B"
                                name="Communication"
                                strokeWidth={1}
                                dot={{ r: 3 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="teamwork"
                                stroke="#8B5CF6"
                                name="Teamwork"
                                strokeWidth={1}
                                dot={{ r: 3 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Skills Comparison - Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Self-Review Skills */}
                {selfSkillsData.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Latest Self-Review Skills
                        </h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={selfSkillsData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                                <XAxis
                                    dataKey="skill"
                                    stroke="#6B7280"
                                    style={{ fontSize: '11px' }}
                                    angle={-15}
                                    textAnchor="end"
                                    height={60}
                                />
                                <YAxis
                                    domain={[0, 5]}
                                    stroke="#6B7280"
                                    style={{ fontSize: '12px' }}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="rating" fill="#3B82F6" name="Rating" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {/* Manager Review Skills */}
                {managerSkillsData.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Latest Manager Review Skills
                        </h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={managerSkillsData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                                <XAxis
                                    dataKey="skill"
                                    stroke="#6B7280"
                                    style={{ fontSize: '11px' }}
                                    angle={-15}
                                    textAnchor="end"
                                    height={60}
                                />
                                <YAxis
                                    domain={[0, 5]}
                                    stroke="#6B7280"
                                    style={{ fontSize: '12px' }}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="rating" fill="#10B981" name="Rating" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {/* Radar Chart - Self vs Manager Comparison */}
            {selfReviews.length > 0 && managerReviews.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Skills Comparison: Self vs Manager Review
                    </h3>
                    <ResponsiveContainer width="100%" height={400}>
                        <RadarChart data={radarData}>
                            <PolarGrid stroke="#374151" opacity={0.3} />
                            <PolarAngleAxis
                                dataKey="skill"
                                stroke="#6B7280"
                                style={{ fontSize: '12px' }}
                            />
                            <PolarRadiusAxis
                                angle={90}
                                domain={[0, 5]}
                                stroke="#6B7280"
                                style={{ fontSize: '10px' }}
                            />
                            <Radar
                                name="Self Rating"
                                dataKey="Self Rating"
                                stroke="#3B82F6"
                                fill="#3B82F6"
                                fillOpacity={0.3}
                            />
                            <Radar
                                name="Manager Rating"
                                dataKey="Manager Rating"
                                stroke="#10B981"
                                fill="#10B981"
                                fillOpacity={0.3}
                            />
                            <Legend />
                            <Tooltip content={<CustomTooltip />} />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default PerformanceAnalytics;
